/** @module components/new-fullscreen-gallery */
import _ from "underscore";
import baustein from "baustein";
import browser from "web/script/utils/browser";
import analytics from "web/script/analytics/analytics";
import traversal from "web/script/dom/traversal";
import templateFullscreenGallery from "web/script/templates/components/fullscreen-gallery.html";
import templateFullscreenGalleryRetailerItem from "web/script/templates/components/fullscreen-gallery-retailer-item.html";
import templateFullscreenGalleryOOSItem from "web/script/templates/components/fullscreen-gallery-oos-item.html";
import { gettext } from "web/script/modules/django-i18n";
import "web/script/components/lazy-image";
import "web/script/components/lead-link";
import "web/script/components/slide-control";
import "web/script/components/slide-indicators";

/**
 * @constructor
 * @alias module:components/fullscreen-gallery
 * @extends module:baustein
 */
export default baustein.register(
    "fullscreen-gallery",
    /** @lends module:components/fullscreen-gallery.prototype */
    {
        template: templateFullscreenGallery,

        defaultOptions: {
            items: null,
            selectedIndex: 0,
            zoomEnabled: false,
            retailer: null,
            designer: null,
            designerLink: null,
            viewSimilarEnabled: false,
        },

        init() {
            this.items = [];
            this.selectedItemIndex = this.options.selectedIndex;
            let isMobile = browser.getViewport() <= browser.VIEWPORTS.TABLET;

            if (isMobile && this.options.viewSimilarEnabled) {
                this.addItem({
                    type: "html",
                    content: templateFullscreenGalleryOOSItem({
                        designer: this.options.designer,
                        designerLink: this.options.designerLink,
                    }),
                });
                this.selectedItemIndex = 0;
            }

            this.addItem(this.options.items);

            if (isMobile && this.options.retailer) {
                this.addItem({
                    type: "html",
                    content: templateFullscreenGalleryRetailerItem(this.options.retailer),
                });
            }

            if (this.items.length <= 1 || browser.hasTouch) {
                this.el.classList.add("fullscreen-gallery-hide-navigation");
            }

            this.viewSimilarEnabled = this.options.viewSimilarEnabled;
        },

        getInitialRenderContext() {
            let seeNewItemsByDesignerHTML;
            let atRetailerText;

            if (this.viewSimilarEnabled) {
                // 'See New In from <em><a {designer_link_props}>{designer_name}</a></em>'
                seeNewItemsByDesignerHTML = gettext(
                    "fullscreen_gallery.see_new_items_by_designer",
                    {
                        designer_link_props: `href="${this.options.designerLink}"`,
                        designer_name: this.options.designer,
                    }
                );
            } else {
                seeNewItemsByDesignerHTML = "";
            }

            if (this.options.retailer) {
                // 'At {retailer_name}'
                atRetailerText = gettext("fullscreen_gallery.at_retailer", {
                    retailer_name: this.options.retailer.name,
                });
            } else {
                atRetailerText = "";
            }

            return {
                options: this.options,
                retailer: this.options.retailer,
                items: this.items,
                selectedItemIndex: this.selectedItemIndex,
                viewSimilarEnabled: this.viewSimilarEnabled,
                // 'Sold Out'
                soldOutTitle: gettext("product_card.sold_out"),
                // 'See Similar Items'
                seeSimilarItemsBtnText: gettext("fullscreen_gallery.see_similar_items_btn_text"),
                // 'or'
                orSeparator: gettext("in_stock_product.or_separator"),
                // 'See New In from <em><a {designer_link_props}>{designer_name}</a></em>'
                seeNewItemsByDesignerHTML: seeNewItemsByDesignerHTML,
                // 'At {retailer_name}'
                atRetailerText: atRetailerText,
                // 'Buy'
                buyBtnText: gettext("fullscreen_gallery.buy_btn_text"),
                // 'Close Gallery'
                closeBtnText: gettext("fullscreen_gallery.close_btn_text"),
            };
        },

        onInsert() {
            this.invoke(this.findComponent("slide-control"), "refresh");

            this.setGlobalHandler("keyup", this._onKeyUp);
            this.emit("overlayOpen");
        },

        onRemove() {
            this.releaseGlobalHandler("keyup", this._onKeyUp);
        },

        setupEvents(add) {
            add("click", this._onOverlayClick);
            add("click", "[data-item-id]", this._onImageThumbnailClick);
            add("click", ".fullscreen-overlay-close", this._onCloseIntent);
            add("click", ".fullscreen-overlay__view-similar a", this._onViewSimilarClick);

            add("currentSlideChange", "slide-control", this._onSlideChange);
            add("zoomStateStart", "slide-control", this._onSlideZoomStart);
            add("zoomStateEnd", "slide-control", this._onSlideZoomEnd);

            add("click", "[data-prev-button]", this._onStepThroughButtonClick);
            add("click", "[data-next-button]", this._onStepThroughButtonClick);
        },

        /**
         * When you press a certain key, go next/previous or even close the overlay.
         * @param event
         * @private
         */
        _onKeyUp(event) {
            var key = event.keyCode;

            switch (key) {
                // escape key
                case 27:
                    this._onCloseIntent(event);
                    break;
            }
        },

        _onSlideChange(event) {
            this._setSelectedIndex(event.index);
        },

        _onSlideZoomStart() {
            this.el.querySelector(".fullscreen-overlay-indicators").classList.add("hidden");
            analytics.event("engagement", "fullscreen_gallery", "zoom_in");
        },

        _onSlideZoomEnd() {
            this.el.querySelector(".fullscreen-overlay-indicators").classList.remove("hidden");
            analytics.event("engagement", "fullscreen_gallery", "zoom_out");
        },

        /**
         * When you click on the image thumbnail, go to that image.
         * @param event
         * @private
         */
        _onImageThumbnailClick(event) {
            event.preventDefault();

            const link = traversal.closest(event.target, "a");
            const itemId = parseInt(
                link.getAttribute("item-id") || link.getAttribute("data-item-id"),
                10
            );
            // analytics handled in `image-gallery.js`
            this.emit("thumbnailClick", { index: itemId });
            this.findComponent("slide-control").goToSlide(itemId);
            this._setSelectedIndex(itemId);
        },

        /**
         * Updates the selected index, the thumbnails and the indicator
         * @private
         */
        _setSelectedIndex(index) {
            this.selectedItemIndex = index;
            this._updateSelectedThumbnail();
            var slideIndicator = this.findComponent("slide-indicators");
            if (slideIndicator) {
                slideIndicator.setSelectedIndex(index);
            }
        },

        _updateSelectedThumbnail() {
            const thumbails = this.el.querySelectorAll(".fullscreen-overlay-thumbnail");

            Array.from(thumbails).forEach((thumbnail) => {
                const itemId = parseInt(thumbnail.getAttribute("data-item-id"), 10);

                thumbnail.classList.remove("loading");
                thumbnail.classList.toggle("active", itemId == this.selectedItemIndex);
            });
        },

        _onStepThroughButtonClick(ev) {
            const isNext = traversal.closest(ev.target, "[data-next-button]");

            if (isNext) {
                this.findComponent("slide-control").next();
            } else {
                this.findComponent("slide-control").previous();
            }
        },

        /**
         * Adds a single item or a list of items to the currents stack
         * @param item {object} or {array}
         */
        addItem(item) {
            if (_.isArray(item)) {
                // when we add a array we want to add all elements at the end of the current stake
                this.items.push(...item);
            } else if (_.isObject(item)) {
                // if it's a single item just add it to the end
                this.items.push(item);
            }
        },

        /**
         * Provides a single item based on the index
         * @param {number} index
         * @returns {object}
         */
        getItem(index) {
            return this.items[index] || {};
        },

        /**
         * Private method for closing this component, just removes the query
         * parameter from the url. Whoever created this component instance
         * should be listening for the removal of this parameter and destroy me.
         * @param event
         * @private
         */
        close() {
            this.emit("overlayClose");
        },

        _onCloseIntent(event) {
            const emittedCloseIntentEvent = this.emit("overlayCloseIntent");

            // If the `overlayCloseIntent` event has been default prevented,
            // it means it's been already handled and we don't have to
            // close any other overlay.
            if (emittedCloseIntentEvent.defaultPrevented) {
                return;
            }

            event.stopPropagation();

            this.close();
        },

        /**
         * We want to close the gallery when the user clicks on the matte
         * @param event
         * @private
         */
        _onOverlayClick(event) {
            if (
                event.target.classList.contains("fullscreen-overlay") ||
                event.target.classList.contains("slide-control__slide__container")
            ) {
                // slide-control__slide__container is a link which we want to preventDefault
                event.preventDefault();

                this._onCloseIntent(event);
            }
        },

        /**
         * We want to close the gallery when the user clicks to view similar
         * @private
         */
        _onViewSimilarClick() {
            analytics.event("engagement", "fullscreen_gallery", "view_similar");
            this.emit("viewSimilar");
        },
    }
);
